import React, { useState } from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Alert from "react-bootstrap/Alert";
import { useSearchParams, useNavigate } from "react-router-dom";
import { portalResetPassword } from "../../services/portalService";
import InputField from "../../utils/InputField";
import { useToast } from '../../contexts/ToastContext';
import { useLoading } from "../../contexts/LoadingContext";

function PortalResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();

  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState("");

  const handleFieldChange = (field, value) => {
    if (field === "newPassword") setNewPassword(value);
    if (field === "confirmPassword") setConfirmPassword(value);
  };

  const validateForm = () => {
    const errors = {};
    if (!newPassword || newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters";
    }
    if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    if (!token) {
      setServerError("Invalid or missing reset token.");
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      startLoading();
      await portalResetPassword(token, newPassword);
      showToast('Password reset successful! You can now log in.', 'success');
      navigate("/portal/login");
    } catch (error) {
      showToast(error.message || "Failed to reset password.", 'error');
      setServerError(error.message || "Failed to reset password.");
    } finally {
      stopLoading();
    }
  };

  return (
    <section>
      <Container
        className="d-flex align-items-center justify-content-center login-background"
        fluid
      >
        <div className="login-modal">
          <div className="d-flex flex-column align-items-center justify-content-center mb-3">
            <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="Logo" className="login-logo mb-2" />
            <h4 className="text-secondary text-center mt-2 fw-semibold">Reset Password</h4>
          </div>

          {serverError && <Alert variant="danger">{serverError}</Alert>}
          {!token && (
            <Alert variant="warning">
              No reset token found in URL. Please check the link you received in your email.
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Row className="px-0 mb-3">
              <InputField
                field="newPassword"
                label="New Password"
                type="password"
                value={newPassword}
                onChange={handleFieldChange}
                error={validationErrors.newPassword}
                as={Col}
                controlId="portalResetNewPassword"
                disabled={!token}
              />
            </Row>
            <Row className="px-0 mb-3">
              <InputField
                field="confirmPassword"
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={handleFieldChange}
                error={validationErrors.confirmPassword}
                as={Col}
                controlId="portalResetConfirmPassword"
                disabled={!token}
              />
            </Row>
            <Button
              className="login-button mt-3 w-100"
              size="xl"
              type="submit"
              disabled={!token}
            >
              Reset Password
            </Button>
            <div className="text-center mt-3">
              <a
                href="#login"
                className="text-decoration-none text-muted small"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/portal/login");
                }}
              >
                Back to Log In
              </a>
            </div>
          </Form>
        </div>
      </Container>
    </section>
  );
}

export default PortalResetPassword;
